import { jsx, jsxs } from "react/jsx-runtime";
import assert from "assert";
import { useContext, useRef, useEffect } from "react";
import { Box } from "../Box/Box.mjs";
import { Text } from "../Text/Text.mjs";
import { touchableText } from "../../css/typography.css.mjs";
import { normalizeKey } from "../private/normalizeKey.mjs";
import { MenuRendererItemContext } from "../MenuRenderer/MenuRendererItemContext.mjs";
import { actionTypes } from "../MenuRenderer/MenuRenderer.actions.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { atoms } from "../../css/atoms/atoms.mjs";
import { iconSize } from "../../hooks/useIcon/index.mjs";
import { menuItem } from "./useMenuItem.css.mjs";
import { MenuRendererContext } from "../MenuRenderer/MenuRendererContext.mjs";
import { useBraidTheme } from "../BraidProvider/BraidThemeContext.mjs";
import { iconSlotSpace } from "../private/iconSlotSpace.mjs";
import { badgeSlotSpace } from "../private/badgeSlotSpace.mjs";
const {
  MENU_ITEM_UP,
  MENU_ITEM_DOWN,
  MENU_ITEM_ESCAPE,
  MENU_ITEM_TAB,
  MENU_ITEM_ENTER,
  MENU_ITEM_SPACE,
  MENU_ITEM_CLICK,
  MENU_ITEM_HOVER
} = actionTypes;
const menuItemChildrenSize = "standard";
function useMenuItem({
  displayName = "MenuItem",
  formElement = false,
  tone,
  onClick,
  data,
  id,
  ...restProps
}) {
  const menuRendererItemContext = useContext(MenuRendererItemContext);
  assert(
    menuRendererItemContext !== null,
    `${displayName} must be rendered as an immediate child of a menu. See the documentation for correct usage: https://seek-oss.github.io/braid-design-system/components/MenuItem`
  );
  if (menuRendererItemContext === null) {
    throw new Error(`${displayName} element rendered outside menu context`);
  }
  const { isHighlighted, index, dispatch, focusTrigger } = menuRendererItemContext;
  const menuItemRef = useRef(null);
  useEffect(() => {
    var _a;
    if (isHighlighted) {
      (_a = menuItemRef.current) == null ? void 0 : _a.focus();
    }
  }, [isHighlighted]);
  const onKeyDown = (event) => {
    const targetKey = normalizeKey(event);
    if (targetKey === "Tab") {
      dispatch({ type: MENU_ITEM_TAB });
    }
    const isArrowPress = targetKey.indexOf("Arrow") === 0;
    const isActionKeyPress = targetKey === "Enter" || targetKey === " ";
    if (isArrowPress || isActionKeyPress) {
      event.preventDefault();
    }
  };
  const onKeyUp = (event) => {
    var _a;
    const targetKey = normalizeKey(event);
    const closeActionKeys = ["Enter", " ", "Escape"];
    const action = {
      ArrowDown: { type: MENU_ITEM_DOWN },
      ArrowUp: { type: MENU_ITEM_UP },
      Enter: { type: MENU_ITEM_ENTER, formElement, index, id },
      " ": { type: MENU_ITEM_SPACE, formElement, index, id },
      Escape: { type: MENU_ITEM_ESCAPE }
    };
    if (action[targetKey]) {
      dispatch(action[targetKey]);
    }
    if (targetKey === "Enter" || targetKey === " ") {
      (_a = menuItemRef.current) == null ? void 0 : _a.click();
    }
    if (!formElement && closeActionKeys.indexOf(targetKey) > -1 || formElement && targetKey === "Escape") {
      focusTrigger();
    }
  };
  const hoverBackground = tone === "critical" ? "criticalLight" : "formAccentSoft";
  return {
    MenuItemChildren,
    menuItemProps: {
      role: "menuitem",
      tabIndex: -1,
      ref: menuItemRef,
      id,
      onKeyUp,
      onKeyDown,
      onMouseEnter: () => dispatch({ type: MENU_ITEM_HOVER, value: index }),
      onClick: (event) => {
        event.stopPropagation();
        dispatch({ type: MENU_ITEM_CLICK, formElement, index, id });
        if (typeof onClick === "function") {
          onClick();
        }
      },
      background: isHighlighted ? hoverBackground : void 0,
      className: [
        menuItem,
        touchableText[menuItemChildrenSize],
        atoms({
          display: "block",
          width: "full",
          paddingX: "small",
          cursor: "pointer",
          textAlign: "left",
          outline: "none"
        })
      ],
      ...buildDataAttributes({ data, validateRestProps: restProps })
    }
  };
}
function MenuItemChildren({
  icon,
  tone,
  children,
  badge,
  formElement = false
}) {
  const menuRendererContext = useContext(MenuRendererContext);
  const legacy = useBraidTheme().legacy;
  const iconSpace = legacy ? "small" : iconSlotSpace;
  const badgeSpace = legacy ? "small" : badgeSlotSpace;
  assert(
    menuRendererContext !== null,
    `MenuItem must be rendered as an immediate child of a menu. See the documentation for correct usage: https://seek-oss.github.io/braid-design-system/components/MenuItem`
  );
  assert(
    // @ts-expect-error
    !badge || badge.type.__isBadge__,
    `MenuItem badge prop can only be an instance of Badge. e.g. <MenuItem badge={<Badge>New</Badge>}>`
  );
  let leftSlot = null;
  if (!formElement) {
    if (icon) {
      leftSlot = /* @__PURE__ */ jsx(
        Text,
        {
          size: menuItemChildrenSize,
          baseline: false,
          tone: tone === "critical" ? tone : void 0,
          children: icon
        }
      );
    } else if (menuRendererContext.reserveIconSpace) {
      leftSlot = /* @__PURE__ */ jsx(Box, { component: "span", display: "block", className: iconSize() });
    }
  }
  return /* @__PURE__ */ jsxs(Box, { component: "span", display: "flex", alignItems: "center", minWidth: 0, children: [
    leftSlot ? /* @__PURE__ */ jsx(
      Box,
      {
        component: "span",
        paddingRight: iconSpace,
        flexShrink: 0,
        minWidth: 0,
        children: leftSlot
      }
    ) : null,
    /* @__PURE__ */ jsx(Box, { component: "span", minWidth: 0, children: /* @__PURE__ */ jsx(
      Text,
      {
        size: menuItemChildrenSize,
        baseline: false,
        tone: tone === "critical" ? tone : void 0,
        maxLines: 1,
        children
      }
    ) }),
    badge ? /* @__PURE__ */ jsx(
      Box,
      {
        component: "span",
        paddingLeft: badgeSpace,
        flexShrink: 0,
        minWidth: 0,
        children: badge
      }
    ) : null
  ] });
}
export {
  useMenuItem
};
